import React, { useMemo } from "react";

/**
 * A row's series as a bare line: `value` is { periodKey: count }, `keys` the
 * periods in order (from the column config), `interval` their grain. No axes,
 * no ticks, no grid — the shape and nothing else.
 *
 * Deliberately a hand-drawn <polyline>, NOT the shared TrendLineChart. One
 * chart per row means one per VISIBLE row at any moment, and a virtualised
 * table mounts and unmounts rows continuously as it scrolls. A recharts tree
 * (responsive container + its ResizeObserver, the mouse-tracking hook that
 * attaches a scroll listener to every ancestor, its own state and memos) is
 * far too much to build and tear down on a scroll frame: measured on
 * ShikshaLokam staging, 44 of them dragged a 271-row scroll down to 7 fps
 * with the main thread blocked 93% of the time. The same scroll with this
 * polyline is ~33 fps and 10%.
 *
 * The viewBox is a fixed 100x100 stretched to the cell (preserveAspectRatio
 * "none"), so the line fills whatever width the column has; the stroke opts
 * out of that scaling so it stays an even weight.
 */
// Room above and below inside the viewBox so a peak or trough isn't clipped
// by half a stroke.
const INSET = 8;

const TableSparkline = ({ value, keys = [], interval, color = "currentColor", height = 28 }) => {
  const points = useMemo(() => {
    if (keys.length < 2) return null;
    const counts = keys.map((k) => value?.[k] || 0);
    const max = Math.max(...counts);
    const min = Math.min(...counts);
    // A flat series has no span to scale by — draw it down the middle.
    const span = max - min || 1;
    const flat = max === min;
    return counts
      .map((count, i) => {
        const x = (i / (keys.length - 1)) * 100;
        const y = flat ? 50 : 100 - INSET - ((count - min) / span) * (100 - INSET * 2);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }, [value, keys]);

  if (!points || !["month", "week", "day"].includes(interval)) return null;
  return (
    <svg
      width="100%"
      height={height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default TableSparkline;
